/**
 * The status registry: timed modifiers on any combatant (PRD #784, slice B).
 *
 * A status is one registry entry, not a field. Aegis damage reduction, a Web
 * slow, and a Burning damage-over-time are all rows here, which is what lets
 * `aegisRemainingTicks` and every field shaped like it disappear.
 *
 * Two rules make that hold.
 *
 * First, modifiers are declarative. A definition says how it adjusts damage
 * taken, damage dealt, movement speed, and resource regeneration, and how much
 * damage it deals per tick. It never says which consumer should read it, so a
 * new consumer folds {@link effectiveModifiers} once instead of adding a read
 * site per status.
 *
 * Second, no exported function here branches on a specific status id. A status
 * id is content identity: it appears in kernel source only as the `id` of an
 * authored definition, and a test in `statuses.test.ts` proves that mechanically
 * over every file in `src/sim/combat/`.
 *
 * Modifier values are declared per point of magnitude. Aegis applies
 * `damage-reduction` at magnitude 50 and the definition declares
 * `damageTakenPercent: -1`, so the fold produces fifty percent less damage
 * taken. Keeping magnitude on the application rather than the definition is
 * what lets one `slow` entry serve a weak web and a strong one.
 *
 * Magnitude is authored tuning and the fold reports it as declared. A magnitude
 * of zero is a legal application that adjusts nothing, and nothing here clamps
 * the folded result, so a `damage-reduction` magnitude above 100 folds past
 * total immunity. Clamping belongs to the consumer: this module cannot know
 * whether minus 140 percent damage taken should floor at zero damage, heal, or
 * be a tuning error, and silently rewriting authored numbers would hide it.
 *
 * This module owns no runtime state and nothing consumes it yet: damage
 * resolution and the Warrior migration land in later slices.
 */

import { DAMAGE_TYPES, type DamageType } from './damageTypes';

/** A stable status id. Ids are content identity and never change once shipped. */
export type StatusId = string;

/**
 * What a second application of a running status does.
 * `refresh` restarts the one instance, `stack` adds another with its own clock,
 * and `ignore` drops the reapplication.
 *
 * A `refresh` never weakens what is already running: remaining ticks and
 * magnitude each take the larger of the running instance and the new
 * application. Last-wins would let a weak proc downgrade and truncate a strong
 * buff, so a monster applying a token slow could cancel a player's Aegis by
 * reapplying the same status, and no caller could see it happen.
 */
export type StatusStacking = 'refresh' | 'stack' | 'ignore';

export const STATUS_STACKINGS: readonly StatusStacking[] = ['refresh', 'stack', 'ignore'];

/**
 * Declared adjustments, each per point of magnitude.
 *
 * Percent fields are additive percentage points, so two statuses at minus
 * twenty fold to minus forty rather than to a product. Additive folding is
 * commutative, which is what makes the fold order-independent by construction
 * rather than by convention.
 */
export interface StatusModifiers {
  damageTakenFlat?: number;
  damageTakenPercent?: number;
  damageDealtFlat?: number;
  damageDealtPercent?: number;
  moveSpeedPercent?: number;
  resourceRegenFlat?: number;
  /** Damage dealt to the bearer each tick, emitted as a payload by {@link tickStatuses}. */
  damageOverTimeFlat?: number;
  /**
   * The wheel entry the per-tick damage is made of (PRD #804).
   *
   * A Burning tick is fire and a Poisoned tick is poison, so each resists
   * against its own entry on the bearer's record at the one damage-taken read
   * site, exactly as a weapon hit of that type would. It is a qualifier on
   * `damageOverTimeFlat` rather than an adjustment of its own, and a tick that
   * declares no type keeps PRD #803's rule: it resists against nothing.
   */
  damageOverTimeType?: DamageType;
  /**
   * Per-type resist adjustment in percentage points per point of magnitude
   * (PRD #804). Negative is a vulnerability, which is what Corpse Skin is for.
   * Read at the damage-taken site beside the derived resist, never folded into
   * the derived record, so it has exactly one read site like every other
   * damage-side modifier.
   */
  resistsFlat?: Partial<Record<DamageType, number>>;
}

const NUMERIC_MODIFIER_KEYS = [
  'damageTakenFlat',
  'damageTakenPercent',
  'damageDealtFlat',
  'damageDealtPercent',
  'moveSpeedPercent',
  'resourceRegenFlat',
  'damageOverTimeFlat',
] as const;

const MODIFIER_KEYS: readonly (keyof StatusModifiers)[] = [
  ...NUMERIC_MODIFIER_KEYS,
  'damageOverTimeType',
  'resistsFlat',
];

/** The folded adjustments a consumer reads. Damage over time is not here: it is a payload. */
export interface EffectiveModifiers {
  damageTakenFlat: number;
  damageTakenPercent: number;
  damageDealtFlat: number;
  damageDealtPercent: number;
  moveSpeedPercent: number;
  resourceRegenFlat: number;
  /** Percentage points added to the bearer's resist, per damage type. */
  resistsFlat: Record<DamageType, number>;
}

const FOLDED_KEYS: readonly Exclude<keyof EffectiveModifiers, 'resistsFlat'>[] = [
  'damageTakenFlat',
  'damageTakenPercent',
  'damageDealtFlat',
  'damageDealtPercent',
  'moveSpeedPercent',
  'resourceRegenFlat',
];

export interface StatusDefinition {
  id: StatusId;
  /** The authored duration, used when an application does not override it. */
  durationTicks: number;
  stacking: StatusStacking;
  modifiers: StatusModifiers;
}

export type StatusCatalog = ReadonlyMap<StatusId, StatusDefinition>;

/** One running instance on a combatant. */
export interface ActiveStatus {
  id: StatusId;
  remainingTicks: number;
  magnitude: number;
}

/**
 * Anything carrying active statuses. A `Combatant` satisfies this structurally,
 * so this module never needs to import it and the dependency stays one way.
 */
export interface StatusHolder {
  statuses: ActiveStatus[];
}

/** One application, as an ability's `applyStatus` payload describes it. */
export interface StatusApplication {
  statusId: StatusId;
  /** Overrides the authored duration. */
  durationTicks?: number;
  /** Scales every declared modifier. Defaults to one. */
  magnitude?: number;
}

export type StatusApplicationResult =
  | { ok: true; outcome: 'applied' | 'refreshed' | 'stacked'; status: ActiveStatus }
  | {
      ok: false;
      reason: 'unknown-status' | 'ignored' | 'invalid-duration' | 'invalid-magnitude';
    };

/** A damage-over-time tick, handed to the caller instead of applied to health here. */
export interface StatusTickPayload {
  statusId: StatusId;
  kind: 'damage';
  amount: number;
  /**
   * What the tick is made of, when the status declared a type. The caller's
   * damage read site resists it per type; an untyped tick resists against
   * nothing, exactly as PRD #803 left it.
   */
  damageType?: DamageType;
}

export interface StatusTickResult {
  payloads: StatusTickPayload[];
  expired: ActiveStatus[];
}

const ID_PATTERN = /^[a-z0-9]+(?:[-.][a-z0-9]+)*$/;

function isPositiveInteger(value: unknown): boolean {
  return Number.isSafeInteger(value) && (value as number) > 0;
}

function isFiniteNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Validate one authored status and return every problem found, so a guard
 * reports the whole picture rather than the first failure. Defensive
 * throughout: a malformed hand-authored entry is reported, never thrown on.
 */
export function validateStatus(definition: StatusDefinition): string[] {
  const problems: string[] = [];
  if (definition === null || typeof definition !== 'object') {
    return ['status must be an object'];
  }
  if (typeof definition.id !== 'string' || !ID_PATTERN.test(definition.id)) {
    problems.push('id must be a non-empty stable string');
  }
  if (!isPositiveInteger(definition.durationTicks)) {
    problems.push('durationTicks must be a positive integer');
  }
  if (!STATUS_STACKINGS.includes(definition.stacking)) {
    problems.push(`stacking must be one of ${STATUS_STACKINGS.join(', ')}`);
  }
  const modifiers = definition.modifiers;
  if (modifiers === null || typeof modifiers !== 'object') {
    problems.push('modifiers must be declared');
    return problems;
  }
  const declared = Object.keys(modifiers);
  for (const key of declared) {
    if (!MODIFIER_KEYS.includes(key as keyof StatusModifiers)) {
      problems.push(`modifier ${key} is not a known modifier`);
      continue;
    }
    if (
      NUMERIC_MODIFIER_KEYS.includes(key as (typeof NUMERIC_MODIFIER_KEYS)[number]) &&
      !isFiniteNumber(modifiers[key as (typeof NUMERIC_MODIFIER_KEYS)[number]])
    ) {
      problems.push(`modifier ${key} must be a finite number`);
    }
  }
  if (modifiers.damageOverTimeType !== undefined) {
    if (!DAMAGE_TYPES.includes(modifiers.damageOverTimeType)) {
      problems.push(
        `modifier damageOverTimeType must be one of ${DAMAGE_TYPES.join(', ')}`,
      );
    }
    // A type with no per-tick damage qualifies nothing. Rejecting it keeps the
    // registry from carrying a claim no tick will ever make.
    if (!((modifiers.damageOverTimeFlat ?? 0) > 0)) {
      problems.push('modifier damageOverTimeType requires damageOverTimeFlat');
    }
  }
  if (modifiers.resistsFlat !== undefined) {
    if (modifiers.resistsFlat === null || typeof modifiers.resistsFlat !== 'object') {
      problems.push('modifier resistsFlat must be a record of damage types');
    } else {
      const entries = Object.entries(modifiers.resistsFlat);
      if (entries.length === 0) {
        problems.push('modifier resistsFlat must declare at least one damage type');
      }
      for (const [type, value] of entries) {
        if (!DAMAGE_TYPES.includes(type as DamageType)) {
          problems.push(`modifier resistsFlat ${type} is not a damage type`);
        } else if (!isFiniteNumber(value)) {
          problems.push(`modifier resistsFlat ${type} must be a finite number`);
        }
      }
    }
  }
  // The type qualifier alone adjusts nothing, so it does not save an otherwise
  // empty record from being inert.
  if (declared.filter((key) => key !== 'damageOverTimeType').length === 0) {
    problems.push('modifiers must declare at least one adjustment');
  }
  if ((modifiers.damageOverTimeFlat ?? 0) < 0) {
    problems.push('modifier damageOverTimeFlat must not be negative');
  }
  return problems;
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    for (const nested of Object.values(value)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
}

/**
 * The authored statuses.
 *
 * `damage-reduction` is what Aegis becomes: refresh stacking, because recasting
 * a defensive buff restarts it rather than doubling it. `slow` is the movement
 * counterpart the Web and the Snare Trap use.
 *
 * The four elemental statuses land here in PRD #804, finally consuming the
 * damage types PRD #803 shipped. Each declares one character, per #713:
 * Burning is fast fire damage over time, Poisoned is slow poison attrition,
 * Chilled takes movement, and Shocked amplifies every hit that follows. The
 * two damaging ones type their ticks, so a Burning tick resists against fire
 * and a Poisoned tick against poison at the one damage-taken read site.
 *
 * `corpse-skin` and `wraith-form` are the Necromancer's two authored effects:
 * the first is the resist-reduction setup the class sequences its kit around,
 * and the second is the movement-and-mitigation form filling the defensive
 * slot. Every number is first-pass and owned by the tuning PRD (#799).
 */
const AUTHORED_STATUSES: readonly StatusDefinition[] = [
  {
    id: 'damage-reduction',
    durationTicks: 100,
    stacking: 'refresh',
    modifiers: { damageTakenPercent: -1 },
  },
  {
    id: 'slow',
    durationTicks: 40,
    stacking: 'refresh',
    modifiers: { moveSpeedPercent: -1 },
  },
  {
    // Fire: a fast burn that stacks, so repeated hits ramp rather than reset.
    id: 'burning',
    durationTicks: 40,
    stacking: 'stack',
    modifiers: { damageOverTimeFlat: 1, damageOverTimeType: 'fire' },
  },
  {
    // Cold: movement, not health. Refresh, because a second chill on a body
    // already chilled is the same cold, held longer.
    id: 'chilled',
    durationTicks: 60,
    stacking: 'refresh',
    modifiers: { moveSpeedPercent: -1 },
  },
  {
    // Poison: half the rate of a burn over three times the clock, which is the
    // attrition the Necromancer is built on. Stacks, so rot layered on rot is
    // the class doing its job rather than a wasted cast.
    id: 'poisoned',
    durationTicks: 120,
    stacking: 'stack',
    modifiers: { damageOverTimeFlat: 0.5, damageOverTimeType: 'poison' },
  },
  {
    // Energy: no damage of its own. It makes everything else hit harder, which
    // is what an overloaded nervous system is worth on the wheel.
    id: 'shocked',
    durationTicks: 60,
    stacking: 'refresh',
    modifiers: { damageTakenPercent: 1 },
  },
  {
    // The Necromancer's setup: strips Poison and Fire resist, in percentage
    // points per point of magnitude, so everything the class does afterwards
    // lands harder. It deals nothing itself, and that is the point.
    id: 'corpse-skin',
    durationTicks: 200,
    stacking: 'refresh',
    modifiers: { resistsFlat: { poison: -1, fire: -1 } },
  },
  {
    // The Necromancer's defensive slot: faster and harder to hurt for the
    // duration, which changes how the class moves rather than only what it
    // survives.
    id: 'wraith-form',
    durationTicks: 160,
    stacking: 'refresh',
    modifiers: { moveSpeedPercent: 1, damageTakenPercent: -0.5 },
  },
];

/** The one registry, keyed by stable status id and frozen at module load. */
export const statusCatalog: StatusCatalog = new Map(
  AUTHORED_STATUSES.map((definition) => [definition.id, deepFreeze({ ...definition })]),
);

/** Look up a status definition. */
export function status(
  id: StatusId,
  catalog: StatusCatalog = statusCatalog,
): StatusDefinition | null {
  return catalog.get(id) ?? null;
}

/**
 * Whether applying this status can damage its bearer over time.
 *
 * The ability catalog's telegraph law calls this: a monster ability whose only
 * payload applies a burn is damaging in fact, so it must be damaging in law and
 * announce itself first. An unknown id answers true, because an unauthored
 * status cannot promise that it is harmless, and the safe failure for a
 * counterplay guarantee is to demand the telegraph.
 */
export function statusCanDamage(
  id: StatusId,
  catalog: StatusCatalog = statusCatalog,
): boolean {
  const definition = catalog.get(id);
  if (!definition) return true;
  return (definition.modifiers.damageOverTimeFlat ?? 0) > 0;
}

/**
 * The tick order: status id first, then application order among instances that
 * share an id. Application order is meaningful here, because a tick payload is
 * attributable to one instance and the caller sees the instances in the order
 * they landed.
 */
function tickOrder(statuses: readonly ActiveStatus[]): ActiveStatus[] {
  return statuses
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) =>
      a.entry.id === b.entry.id ? a.index - b.index : a.entry.id < b.entry.id ? -1 : 1,
    )
    .map(({ entry }) => entry);
}

/**
 * The fold order: status id, then magnitude. The fold has no attributable
 * instances, only a sum, so ordering it by anything the application sequence
 * decides makes the sum depend on that sequence. Two instances of one stacking
 * status at 0.1 and 0.2 added in the other order land on a different float.
 * Sorting by the values being added is what makes the fold order-independent
 * for same-id stacks and not only across ids.
 */
function foldOrder(statuses: readonly ActiveStatus[]): ActiveStatus[] {
  return [...statuses].sort((a, b) =>
    a.id === b.id ? a.magnitude - b.magnitude : a.id < b.id ? -1 : 1,
  );
}

/**
 * Apply a status to a holder, honoring the authored stacking rule.
 *
 * Inputs are validated rather than trusted: a malformed duration or magnitude
 * is rejected with its own reason instead of producing a status that never
 * expires or one that heals its bearer.
 */
export function applyStatus(
  holder: StatusHolder,
  application: StatusApplication,
  catalog: StatusCatalog = statusCatalog,
): StatusApplicationResult {
  const definition = catalog.get(application.statusId);
  if (!definition) return { ok: false, reason: 'unknown-status' };
  if (application.durationTicks !== undefined && !isPositiveInteger(application.durationTicks)) {
    return { ok: false, reason: 'invalid-duration' };
  }
  const magnitude = application.magnitude ?? 1;
  if (!isFiniteNumber(magnitude) || magnitude < 0) {
    return { ok: false, reason: 'invalid-magnitude' };
  }
  const durationTicks = application.durationTicks ?? definition.durationTicks;
  const running = holder.statuses.find((entry) => entry.id === definition.id);
  if (running && definition.stacking === 'ignore') {
    return { ok: false, reason: 'ignored' };
  }
  if (running && definition.stacking === 'refresh') {
    // Refresh never weakens. Taking the larger of each field means a weak or
    // short reapplication extends nothing and downgrades nothing, so the rule
    // reads the same for a defensive buff and for a monster debuff proc.
    running.remainingTicks = Math.max(running.remainingTicks, durationTicks);
    running.magnitude = Math.max(running.magnitude, magnitude);
    return { ok: true, outcome: 'refreshed', status: running };
  }
  const applied: ActiveStatus = { id: definition.id, remainingTicks: durationTicks, magnitude };
  holder.statuses.push(applied);
  return { ok: true, outcome: running ? 'stacked' : 'applied', status: applied };
}

/**
 * Advance every status by one tick.
 *
 * Damage over time is returned as a payload rather than written to health here:
 * this module knows nothing about health, and keeping the payload out of the
 * mutation keeps death, mitigation, and event emission in the one place that
 * already owns them. A status pays out on the tick it expires, then leaves.
 *
 * An active status the registry no longer knows is dropped as expired, so a
 * removed catalog entry cannot leave a permanent modifier behind.
 */
export function tickStatuses(
  holder: StatusHolder,
  catalog: StatusCatalog = statusCatalog,
): StatusTickResult {
  const payloads: StatusTickPayload[] = [];
  const expired: ActiveStatus[] = [];
  const surviving: ActiveStatus[] = [];
  for (const entry of tickOrder(holder.statuses)) {
    const definition = catalog.get(entry.id);
    if (!definition) {
      expired.push(entry);
      continue;
    }
    const perTick = (definition.modifiers.damageOverTimeFlat ?? 0) * entry.magnitude;
    if (perTick > 0) {
      const damageType = definition.modifiers.damageOverTimeType;
      payloads.push({
        statusId: entry.id,
        kind: 'damage',
        amount: perTick,
        ...(damageType === undefined ? {} : { damageType }),
      });
    }
    entry.remainingTicks -= 1;
    if (entry.remainingTicks <= 0) {
      entry.remainingTicks = 0;
      expired.push(entry);
    } else {
      surviving.push(entry);
    }
  }
  holder.statuses.length = 0;
  holder.statuses.push(...surviving);
  return { payloads, expired };
}

/**
 * Fold the holder's statuses into one adjustment record, in {@link foldOrder}.
 * This is the only read site a consumer needs: nothing downstream asks which
 * statuses are running, only what they add up to.
 *
 * The result is not clamped. See the module docblock: a folded value past a
 * meaningful limit is the consumer's to bound.
 */
export function effectiveModifiers(
  holder: StatusHolder,
  catalog: StatusCatalog = statusCatalog,
): EffectiveModifiers {
  const folded: EffectiveModifiers = {
    damageTakenFlat: 0,
    damageTakenPercent: 0,
    damageDealtFlat: 0,
    damageDealtPercent: 0,
    moveSpeedPercent: 0,
    resourceRegenFlat: 0,
    resistsFlat: {
      physical: 0,
      fire: 0,
      cold: 0,
      poison: 0,
      energy: 0,
    },
  };
  for (const entry of foldOrder(holder.statuses)) {
    const definition = catalog.get(entry.id);
    if (!definition) continue;
    for (const key of FOLDED_KEYS) {
      folded[key] += (definition.modifiers[key] ?? 0) * entry.magnitude;
    }
    const resists = definition.modifiers.resistsFlat;
    if (resists) {
      for (const type of DAMAGE_TYPES) {
        folded.resistsFlat[type] += (resists[type] ?? 0) * entry.magnitude;
      }
    }
  }
  return folded;
}

/** Drop every active status. Used when a combatant is removed or recycled. */
export function clearStatuses(holder: StatusHolder): void {
  holder.statuses.length = 0;
}
